import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AccountsService } from "@/lib/api_client";
import { getAuthHeaders } from "@/lib/api";
import { fetchAll } from "./fetchAll";

/**
 * Per-user feature flags that can be toggled from the user detail page.
 * Keep in sync with the `FeatureFlags` interface in
 * backend/src/modules/accounts/schemas/user-feature-flags.ts.
 */
export const USER_FEATURE_FLAGS = [
  { key: "garmin", label: "Garmin" },
  { key: "kvinnehelse", label: "Kvinnehelse" },
] as const;

export interface UserFeatureFlag {
  id: string;
  authUserId: string;
  features: Record<string, boolean>;
  createdAt: string;
  updatedAt: string;
}

export const useAllFeatureFlags = () =>
  useQuery({
    queryKey: ["user-feature-flags"],
    queryFn: () => fetchAll<UserFeatureFlag>(AccountsService.getApiUserFeatureFlags),
    staleTime: 60_000,
  });

export const useFeatureFlagsByUser = (authUserId: string | undefined) => {
  const { data: allFlags, ...rest } = useAllFeatureFlags();
  const userFlag = allFlags?.find((f) => f.authUserId === authUserId) ?? null;
  return { data: userFlag, allFlags, ...rest };
};

export const useUpdateFeatureFlags = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, features }: { id: string; features: Record<string, boolean> }) => {
      const res = await AccountsService.putApiUserFeatureFlagsById({
        path: { id },
        body: { features },
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to update feature flags");
      return res.data as unknown as UserFeatureFlag;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-feature-flags"] });
    },
  });
};

export const useCreateFeatureFlags = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ authUserId, features }: { authUserId: string; features: Record<string, boolean> }) => {
      const res = await AccountsService.postApiUserFeatureFlags({
        body: { authUserId, features },
        headers: getAuthHeaders(),
      });
      if (!res.data) throw new Error("Failed to create feature flags");
      return res.data as unknown as UserFeatureFlag;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["user-feature-flags"] });
    },
  });
};
